import * as React from "react";
import { Input } from "@/components/ui/input";
import { formatNumberForDisplay } from "@/lib/numberInput";
import { cn } from "@/lib/utils";

export interface NumericInputProps extends Omit<React.ComponentProps<"input">, "type" | "onChange" | "value" | "onBlur"> {
  value?: string | number | null;
  onChange?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  allowDecimals?: boolean;
  inputMode?: "numeric" | "decimal";
  prefix?: React.ReactNode;
}

const NumericInput = React.forwardRef<HTMLInputElement, NumericInputProps>(
  ({ value, onChange, onBlur, allowDecimals = true, inputMode, className, prefix, ...props }, ref) => {
    const [internalValue, setInternalValue] = React.useState<string>(() => formatNumberForDisplay(value));
    const [isFocused, setIsFocused] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    
    React.useEffect(() => {
      if (!isFocused) {
        const displayValue = formatNumberForDisplay(value);
        setInternalValue(displayValue);
      }
    }, [value, isFocused]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      
      if (inputValue === "") {
        setInternalValue("");
        onChange?.("");
        return;
      }

      let cleaned = inputValue.replace(/[^\d,.]/g, "");
      
      if (!allowDecimals) {
        cleaned = cleaned.replace(/[,.]/g, "");
        setInternalValue(cleaned);
        onChange?.(cleaned);
        return;
      }
      
      const lastCommaIndex = cleaned.lastIndexOf(",");
      const lastPeriodIndex = cleaned.lastIndexOf(".");
      
      let separator: string | null = null;
      if (lastCommaIndex !== -1 && lastPeriodIndex !== -1) {
        separator = lastCommaIndex > lastPeriodIndex ? "," : ".";
      } else if (lastCommaIndex !== -1) {
        separator = ",";
      } else if (lastPeriodIndex !== -1) {
        separator = ".";
      }
      
      if (separator) {
        const otherSeparator = separator === "," ? "." : ",";
        cleaned = cleaned.replace(new RegExp(`\\${otherSeparator}`, "g"), "");
        
        const parts = cleaned.split(separator);
        if (parts.length >= 1) {
          const integerPart = parts[0].replace(/[^\d]/g, "");
          if (parts.length > 1) {
            const decimalPart = parts.slice(1).join("").replace(/[^\d]/g, "").slice(0, 2);
            cleaned = integerPart + separator + decimalPart;
          } else {
            cleaned = integerPart + separator;
          }
        }
      } else {
        cleaned = cleaned.replace(/[^\d]/g, "");
      }
      
      setInternalValue(cleaned);
      
      const storageValue = cleaned.replace(",", ".");
      onChange?.(storageValue);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      
      if (internalValue === "" || internalValue.trim() === "") {
        setInternalValue("0");
        onChange?.("0");
      }
      
      onBlur?.(e);
    };

    const displayValue = internalValue;

    return (
      <div className="relative">
        {prefix ? (
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
            {prefix}
          </span>
        ) : null}
        <Input
          ref={(node) => {
            inputRef.current = node;
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          type="text"
          inputMode={inputMode || (allowDecimals ? "decimal" : "numeric")}
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cn(prefix ? "pl-14" : "", className)}
          {...props}
        />
      </div>
    );
  }
);

NumericInput.displayName = "NumericInput";

export { NumericInput };
